/**
 * The inbox's cache (`MCK-20260521-009` B3).
 *
 * **Its own key, not a workspace's.** The list spans every studio the person works for, so a
 * key under one of them would be a lie the switcher would then have to invalidate. It hangs
 * off the session, like `/tasks` does.
 *
 * **Nothing polls and nothing subscribes.** The summary is read when a screen mounts and swept
 * by every mutation that could change it; a bell that polled every thirty seconds would be one
 * request per user per thirty seconds for a number that changes a few times a day (CLAUDE.md,
 * data fetching).
 */

import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { SESSION_QUERY_KEY } from '@/features/session';

import type { InboxItem, InboxPage, InboxScope, InboxTab } from './api';
import { listInbox, markInbox, readInboxSummary } from './api';

const INBOX_KEY = [...SESSION_QUERY_KEY, 'inbox'] as const;
/** Every list, whatever its tab, filter and scope. The prefix `useMarkInbox` patches. */
const LIST_KEY = [...INBOX_KEY, 'list'] as const;
/** Where `unreadOnly` sits in a list key: `[...INBOX_KEY, 'list', tab, unreadOnly, scope]`. */
const UNREAD_ONLY_AT = LIST_KEY.length + 1;

export function inboxListQueryOptions(
  tab: InboxTab | null,
  unreadOnly: boolean,
  scope: InboxScope,
) {
  return infiniteQueryOptions({
    // The scope is part of the key: *my* mail and *my firm's* are two collections, and a shared
    // entry would show one while the tab strip counted the other.
    queryKey: [...INBOX_KEY, 'list', tab, unreadOnly, scope] as const,
    queryFn: ({ pageParam }) => listInbox(tab, unreadOnly, scope, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? null,
  });
}

export function inboxSummaryQueryOptions(scope: InboxScope = 'me') {
  return queryOptions({
    queryKey: [...INBOX_KEY, 'summary', scope] as const,
    queryFn: () => readInboxSummary(scope),
    // A minute: the number is read by the bell on every screen, and a person who has just been
    // assigned something does not need to learn it in the same second.
    staleTime: 60_000,
  });
}

export function useInboxList(tab: InboxTab | null, unreadOnly: boolean, scope: InboxScope) {
  return useInfiniteQuery(inboxListQueryOptions(tab, unreadOnly, scope));
}

/**
 * The counts for whichever scope is on screen.
 *
 * **The bell asks without one**, deliberately: it is the count of what *you* owe, and a bell
 * lit by a colleague's unread mention is a bell people turn off.
 */
export function useInboxSummary(enabled = true, scope: InboxScope = 'me') {
  return useQuery({ ...inboxSummaryQueryOptions(scope), enabled });
}

type InboxMark = Parameters<typeof markInbox>[0];
type InboxListData = InfiniteData<InboxPage, string | null>;

/**
 * What one mark did to one cached row, or `null` when the row is no longer in that list.
 *
 * `read` is a flip of `read_at`; `dismissed: true` takes the row out of every list, because
 * the server does not return dismissed rows. A row *marked read in an unread-only list* also
 * leaves it — that is what the refetch this replaces would have shown, only a round trip later.
 */
function afterMark(
  item: InboxItem,
  mark: InboxMark,
  unreadOnly: boolean,
  now: string,
): InboxItem | null {
  if (mark.dismissed === true) return null;
  if (mark.read === undefined) return item;
  const read_at = mark.read ? (item.read_at ?? now) : null;
  if (unreadOnly && read_at !== null) return null;
  return read_at === item.read_at ? item : { ...item, read_at };
}

/**
 * Whether a cached row is one of the rows the request named — by id, or by its whole tab —
 * and one the server actually changed: `missed` is the ids that were not the caller's to
 * mark, and a colleague's row must stay exactly as the caller saw it.
 */
function isNamedBy(item: InboxItem, mark: InboxMark, missed: ReadonlySet<string>): boolean {
  if (missed.has(item.id)) return false;
  if (mark.item_ids !== undefined) return mark.item_ids.includes(item.id);
  if (mark.tabs !== undefined) return mark.tabs.includes(item.tab);
  return false;
}

/**
 * Write the mark into every cached list page, in place.
 *
 * `setQueriesData` over the list prefix, never `invalidateQueries` over it: the list is an
 * infinite query, and TanStack refetches **every cached page in sequence** when one is
 * invalidated — ten pages deep is ten requests plus a summary for one checkbox (review
 * 2026-09-25 §3.23; the same trap `features/tasks/queries.ts` rule 2 documents). The rows the
 * server would send back are knowable from the request, so they are written rather than asked
 * for.
 */
function patchInboxLists(
  queryClient: QueryClient,
  mark: InboxMark,
  missed: ReadonlySet<string>,
): void {
  const now = new Date().toISOString();
  for (const [key, data] of queryClient.getQueriesData<InboxListData>({ queryKey: LIST_KEY })) {
    if (data === undefined) continue;
    const unreadOnly = key[UNREAD_ONLY_AT] === true;
    let changed = false;
    const pages = data.pages.map((page) => {
      let pageChanged = false;
      const items: InboxItem[] = [];
      for (const item of page.items) {
        const next = isNamedBy(item, mark, missed) ? afterMark(item, mark, unreadOnly, now) : item;
        if (next !== item) pageChanged = true;
        if (next !== null) items.push(next);
      }
      changed ||= pageChanged;
      return pageChanged ? { ...page, items } : page;
    });
    if (changed) queryClient.setQueryData<InboxListData>(key, { ...data, pages });
  }
}

/**
 * Marking writes the rows and the count, and refetches neither.
 *
 * Both change on every mark — a row leaves the unread filter and two counters move — and the
 * screen draws them side by side, so the two have to land together. They do: the response
 * carries the caller's own summary off the same transaction (`inbox/router.py`), and the rows
 * are patched from the request in the same tick (`patchInboxLists`). What remains is marked
 * stale with `refetchType: 'none'`, so the next mount of a list asks the server again without
 * a storm of page refetches now — and the other scopes' summaries, which the response cannot
 * speak for, are invalidated exactly, one small request each and only if somebody is looking.
 *
 * **One case still refetches: undo of a dismissal.** A row brought back is a row the cache no
 * longer holds and cannot reconstruct, so the *active* lists are refetched the old way. It is
 * the rare gesture and the one the patch cannot express.
 */
export function useMarkInbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markInbox,
    retry: false,
    onSuccess: (result, mark) => {
      queryClient.setQueryData(inboxSummaryQueryOptions('me').queryKey, result.summary);
      for (const scope of ['studio', 'others'] as const) {
        void queryClient.invalidateQueries({
          queryKey: inboxSummaryQueryOptions(scope).queryKey,
          exact: true,
        });
      }
      if (mark.dismissed === false) {
        void queryClient.invalidateQueries({ queryKey: LIST_KEY });
        return;
      }
      patchInboxLists(queryClient, mark, new Set(result.missed));
      void queryClient.invalidateQueries({ queryKey: LIST_KEY, refetchType: 'none' });
    },
  });
}
